import { useEffect, useState } from 'react';
import { useApi } from '@backstage/frontend-plugin-api';
import { discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Content, Link } from '@backstage/core-components';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Card, Grid, Typography, makeStyles } from '@material-ui/core';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import LayersOutlinedIcon from '@material-ui/icons/LayersOutlined';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import LibraryAddOutlinedIcon from '@material-ui/icons/LibraryAddOutlined';
import { fetchPipelineManifest } from './pipelineManifest';
import type { PipelineDescriptor } from './pipelineManifest';
import { TemplateCatalog } from './TemplatesPage';

/**
 * The two things people come to Create for, named the way they would say them.
 *
 * Before this, Create was a grid of equal cards whose titles asked people to
 * self-diagnose ("Deploy a service", "New environment", "Graph-RAG
 * engagement", plus a request card per Control pipeline) — and "engagement"
 * meant a contract with client data in Control and a starter repo here. The hub
 * puts the two real questions first:
 *
 *   - I have, or want, code to ship   → Deploy a service (repo-first, D-25)
 *   - I need somewhere to run things   → New environment (Control's new-env)
 *
 * A client engagement is not a third card: it is an environment that holds a
 * client's real data, and the environment form asks exactly that. Graph-RAG is
 * a starter for a new repository, so it sits under Deploy a service.
 *
 * The cloud guidance on the environment card is read from Control's manifest
 * (`new-env`'s `ui.fields.cloud.options`), not written here — the hint people
 * act on must be the one Control validates against (D-24).
 */
const SERVICE_TEMPLATE = '/create/templates/default/answerrocket-service';
const GRAPHRAG_TEMPLATE =
  '/create/templates/default/answerrocket-graphrag-engagement';
const NEW_ENV = '/create/templates/new-env';

const useStyles = makeStyles(theme => ({
  intro: {
    color: 'var(--ar-fg-2)',
    fontSize: '0.9375rem',
    marginBottom: 20,
    maxWidth: 680,
  },
  // Same card as the form sections (./formKit): 14px radius, hairline edge,
  // the kit's soft shadow — so the hub and the forms it opens read as one.
  primary: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '22px 24px',
    gap: 12,
    borderRadius: '14px !important',
    boxShadow: '0 6px 24px rgba(17, 24, 39, 0.05)',
    transition: 'border-color 160ms cubic-bezier(.22,1,.36,1)',
    '&:hover': { borderColor: theme.palette.primary.main },
  },
  eyebrow: {
    fontFamily: 'var(--ar-font-mono)',
    fontSize: '0.6875rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
    marginBottom: 2,
  },
  primaryHead: { display: 'flex', alignItems: 'center', gap: 12 },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    flex: '0 0 auto',
    borderRadius: 10,
    backgroundColor: 'var(--ar-accent-soft)',
    color: theme.palette.primary.main,
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 660,
    letterSpacing: '-0.011em',
    color: 'var(--ar-fg)',
  },
  body: { color: 'var(--ar-fg-2)', fontSize: '0.875rem', lineHeight: 1.55 },
  choices: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  choice: { fontSize: '0.8125rem', lineHeight: 1.5, color: 'var(--ar-fg-2)' },
  choiceName: { fontWeight: 620, color: 'var(--ar-fg)' },
  foot: {
    marginTop: 'auto',
    paddingTop: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  // Card's own root styles win over a flex layout set on it, so the row
  // lives on an inner element.
  secondary: { height: '100%', borderRadius: '14px !important' },
  secondaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
  },
  secondaryText: { fontSize: '0.8125rem', color: 'var(--ar-fg-2)' },
  secondaryTitle: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 620,
    color: 'var(--ar-fg)',
  },
  sectionTitle: {
    fontSize: '0.8125rem',
    fontWeight: 660,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
    margin: '36px 0 14px',
  },
  plainLink: { textDecoration: 'none', display: 'block', height: '100%' },
}));

/** Cloud options as Control publishes them, in Control's order. */
const cloudChoices = (pipelines?: PipelineDescriptor[]) => {
  const options = pipelines?.find(p => p.id === 'new-env')?.ui.fields?.cloud
    ?.options;
  return options ? Object.entries(options) : [];
};

export const CreateHub = () => {
  const classes = useStyles();
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [pipelines, setPipelines] = useState<PipelineDescriptor[]>();

  useEffect(() => {
    let active = true;
    // Best-effort: the card still works without the hints, and the form it
    // opens says plainly when Control cannot be reached.
    fetchPipelineManifest(discovery, fetchApi)
      .then(p => active && setPipelines(p))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [discovery, fetchApi]);

  const clouds = cloudChoices(pipelines);

  return (
    <Content>
      <Typography className={classes.intro}>
        Ask the platform for something new. Every request opens pull requests
        and waits for a DevOps approval before anything in the cloud changes —
        you can follow each one under My requests.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card className={classes.primary}>
            <div className={classes.primaryHead}>
              <span className={classes.icon}>
                <CloudUploadOutlinedIcon />
              </span>
              <span>
                <div className={classes.eyebrow}>Code → Cloud Run</div>
                <span className={classes.title}>Deploy a service</span>
              </span>
            </div>
            <Typography className={classes.body}>
              Ship code to Cloud Run on the paved road. Bring a repository you
              have already pushed, or start a new one.
            </Typography>
            <ul className={classes.choices}>
              <li className={classes.choice}>
                <span className={classes.choiceName}>From your repository</span>{' '}
                — we check it, open a PR for anything it is missing, then
                request the deploy.
              </li>
              <li className={classes.choice}>
                <span className={classes.choiceName}>From a starter</span> —
                TypeScript or Python, or the{' '}
                <Link to={GRAPHRAG_TEMPLATE}>Graph-RAG accelerator</Link> for
                knowledge-graph + vector search over a document corpus.
              </li>
            </ul>
            <div className={classes.foot}>
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to={SERVICE_TEMPLATE}
              >
                Deploy a service
              </Button>
            </div>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card className={classes.primary}>
            <div className={classes.primaryHead}>
              <span className={classes.icon}>
                <LayersOutlinedIcon />
              </span>
              <span>
                <div className={classes.eyebrow}>AWS account · GCP project</div>
                <span className={classes.title}>New environment</span>
              </span>
            </div>
            <Typography className={classes.body}>
              A new place to run things: its own billing and access boundary.
              Most work belongs in an environment that already exists — ask for
              one only when it needs to be separate.
            </Typography>
            {clouds.length > 0 && (
              <ul className={classes.choices}>
                {clouds.map(([key, option]) => (
                  <li key={key} className={classes.choice}>
                    <span className={classes.choiceName}>{option.label}</span>
                    {option.hint ? ` — ${option.hint}` : ''}
                  </li>
                ))}
              </ul>
            )}
            <Typography className={classes.body}>
              <strong>Client engagement?</strong> If it will hold a client's
              real data, say so on the form — that makes it a time-boxed
              engagement with an end date DevOps can renew.
            </Typography>
            <div className={classes.foot}>
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to={NEW_ENV}
              >
                Request an environment
              </Button>
            </div>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Link to="/create/request" className={classes.plainLink}>
            <Card className={classes.secondary}>
              <div className={classes.secondaryRow}>
                <span className={classes.icon}>
                  <PlaylistAddCheckIcon fontSize="small" />
                </span>
                <span className={classes.secondaryText}>
                  <span className={classes.secondaryTitle}>My requests</span>
                  Where everything you asked for got to.
                </span>
              </div>
            </Card>
          </Link>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Link to="/catalog-import" className={classes.plainLink}>
            <Card className={classes.secondary}>
              <div className={classes.secondaryRow}>
                <span className={classes.icon}>
                  <LibraryAddOutlinedIcon fontSize="small" />
                </span>
                <span className={classes.secondaryText}>
                  <span className={classes.secondaryTitle}>
                    Already running? Register it
                  </span>
                  Add an existing repository's catalog-info.yaml to the catalog.
                </span>
              </div>
            </Card>
          </Link>
        </Grid>
      </Grid>

      <Typography component="h2" className={classes.sectionTitle}>
        Everything you can start
      </Typography>
      <TemplateCatalog />
    </Content>
  );
};
